/**
 * POs Invoices Component
 */

import React from 'react'
import {
	Paper,
	Box,
	Typography,
	Checkbox,
	Stack,
	TextField,
	InputAdornment,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material'
/** TYPES */
import type { Invoice } from '@/types/invoice'
import type { CartItem } from '@/types/pos'
/** SERVICES */
import { fetchInvoices } from '@/hooks/invoice'
/** ICONS */
import ReceiptIcon from '@mui/icons-material/Receipt'
import SearchIcon from '@mui/icons-material/Search'
/** COMPONENTS */
import { DataGrid } from '@mui/x-data-grid'
import { useInvoiceItems } from '@/hooks/invoice.hook'

interface POSInvoicesProps {
	onAddToCart: (items: CartItem[]) => void
	onInvoiceChange?: (invoiceId: string | null) => void
	activeInvoiceId?: string | null
}

const POSInvoices = (props: POSInvoicesProps) => {
	const [invoices, setInvoices] = React.useState<Invoice[]>([])
	const [loading, setLoading] = React.useState<boolean>(false)
	const [total, setTotal] = React.useState<number>(0)
	const [page, setPage] = React.useState<number>(0)
	const [pageSize, setPageSize] = React.useState<number>(10)
	const [searchValue, setSearchValue] = React.useState<string>('')
	const [search, setSearch] = React.useState<string>('')
	const [statusFilter, setStatusFilter] = React.useState<string>('')

	const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<string | null>(null)

	// React.useEffect(() => {
	// 	if (props.activeInvoiceId) {
	// 		setSelectedInvoiceId(props.activeInvoiceId)
	// 	} else {
	// 		setSelectedInvoiceId(null)
	// 	}
	// }, [props.activeInvoiceId])

	const loadInvoices = React.useCallback(async () => {
		setLoading(true)
		try {
			const response = await fetchInvoices({
				page: page + 1,
				limit: pageSize,
				search: search,
				status: statusFilter,
			})
			setInvoices(response.invoices)
			setTotal(response.total)
		} catch (error) {
			console.error('Failed to fetch invoices:', error)
		} finally {
			setLoading(false)
		}
	}, [page, pageSize, search, statusFilter])

	const { fetchInvoiceItems, loading: itemsLoading } = useInvoiceItems()

	// Debounce search
	React.useEffect(() => {
		// debounce
		if (searchValue === search) return

		const timeout = setTimeout(() => {
			setSearch(searchValue)
		}, 500)
		return () => clearTimeout(timeout)
		// eslint-disable-next-line
	}, [searchValue])

	const loadedInvoiceIdRef = React.useRef<string | null>(null)

	React.useEffect(() => {
		// SCENARIO 1: User unchecked the invoice or no invoice is active
		if (!props.activeInvoiceId) {
			if (loadedInvoiceIdRef.current !== null) {
				props.onAddToCart([]) // Clear the cart
				loadedInvoiceIdRef.current = null
			}
			return
		}

		// SCENARIO 2: Tab switch (ID is same as what's already loaded)
		if (props.activeInvoiceId === loadedInvoiceIdRef.current) {
			return
		}

		// SCENARIO 3: A new/different Invoice was selected
		const getItems = async () => {
			const items = await fetchInvoiceItems(props.activeInvoiceId!)
			if (items) {
				// This calls handleSelectInvoiceItems which replaces the cart
				props.onAddToCart(items)
				loadedInvoiceIdRef.current = props.activeInvoiceId!
			}
		}

		getItems()
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.activeInvoiceId, props.onAddToCart, fetchInvoiceItems])

	React.useEffect(() => {
		loadInvoices()
	}, [loadInvoices])

	const columns = [
		{ field: 'uid', headerName: 'Нэхэмжлэх №', width: 150 },
		{
			field: 'customer',
			headerName: 'Харилцагч',
			flex: 1.5,
			renderCell: (params: any) => (
				<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
					<Typography variant="body2" sx={{ mb: 0 }}>
						{params.value?.fullName}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{params.value?.systemId}
					</Typography>
				</Box>
			),
		},
		{
			field: 'contract',
			headerName: 'Гэрээ',
			flex: 1,
			renderCell: (params: any) => (
				<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
					<Typography variant="body2" sx={{ mb: 0 }}>
						{params.value?.uid}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{params.value?.serviceAddress}
					</Typography>
				</Box>
			),
		},
		{ field: 'status', headerName: 'Төлөв', width: 120 },
		{
			field: 'total',
			headerName: 'Нийт дүн',
			flex: 1,
			renderCell: (params: any) => new Intl.NumberFormat('mn-MN').format(params.value) + '₮',
		},
		{
			field: 'paid',
			headerName: 'Төлсөн',
			flex: 1,
			renderCell: (params: any) => new Intl.NumberFormat('mn-MN').format(params.value) + '₮',
		},
		{
			field: 'due',
			headerName: 'Үлдэгдэл',
			flex: 1,
			renderCell: (params: any) => new Intl.NumberFormat('mn-MN').format(params.value) + '₮',
		},
		{
			field: 'select',
			headerName: 'Сонгох',
			width: 100,
			sortable: false,
			filterable: false,
			renderCell: (params: any) => (
				<Checkbox
					checked={props.activeInvoiceId === params.row.id}
					onChange={(e) => {
						const isChecked = e.target.checked
						const newId = isChecked ? params.row.id : null

						// 1. Update local UI state (optional if parent provides prop back)
						setSelectedInvoiceId(newId)

						// 2. CRITICAL: Tell the parent the ID changed
						// This will trigger the useEffect that fetches items
						if (props.onInvoiceChange) {
							props.onInvoiceChange(newId)
						}
					}}
				/>
			),
		},
	]

	return (
		<Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
			<Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
				<Stack direction="row" spacing={1}>
					<TextField
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						size="small"
						placeholder="Нэхэмжлэл хайх..."
						value={searchValue}
						onChange={(e) => setSearchValue(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								setPage(0)
								loadInvoices()
							}
						}}
					/>
					<FormControl sx={{ minWidth: 150 }} size="small">
						<InputLabel>Төлөв</InputLabel>
						<Select
							label="Төлөв"
							size="small"
							value={statusFilter}
							onChange={(e) => setStatusFilter(e.target.value)}
						>
							<MenuItem value="" selected={statusFilter === ''}>
								Бүгд
							</MenuItem>
							<MenuItem value="paid">Төлсөн</MenuItem>
							<MenuItem value="pending">Төлөгдөөгүй</MenuItem>
							<MenuItem value="cancelled">Цуцлагдсан</MenuItem>
						</Select>
					</FormControl>
				</Stack>
			</Box>
			<Box sx={{ height: '100%' }}>
				<DataGrid
					rows={invoices}
					columns={columns}
					loading={loading}
					pageSizeOptions={[10, 20, 50]}
					pagination
					paginationMode="server"
					paginationModel={{
						page,
						pageSize,
					}}
					onPaginationModelChange={(params) => {
						setPage(params.page)
						setPageSize(params.pageSize)
					}}
					rowCount={total}
					sx={{
						border: 0,
					}}
					autoHeight
				/>
			</Box>
		</Paper>
	)
}

export default POSInvoices
